<template>


  <div class="slider" v-if="props.week?.items_photo">


    <div ref="photosEl" class="photos" :size="props.week.items_photo.length">

      <UiCarousel :slides="galleryPhotos.slice(0, 6)" :showCounter="true">
        <template #slide="{ data, index }">
          <div
            class="photo"
            :class="{ 'is-video': data.is_video }"
            @click="handleMediaClick($event, data)"
          >
            <!-- Render a <video loading="lazy"> tag for videos, otherwise the current <picture> markup -->
            <template v-if="data.is_video">
              <!-- Collect ref so IntersectionObserver can control playback -->
              <video loading="lazy"
                :ref="setVideoRef"
                class="autoplay-video"
                :style="mediaStyle"
                :src="data.video"
                :poster="data.size_m"
                muted
                playsinline
                preload="metadata"
                autoplay
                loop
              />
              <!-- <div class="video_play"></div> -->
              <!-- Mute / unmute toggle button -->
              <div class="mute-toggle muted" @click.stop="toggleMute($event)">
                <i class="icon-volume-x"></i>
                <i class="icon-volume-2"></i>
              </div>
            </template>

            <template v-else>
              <picture>
                <source
                  media="(max-width: 600px)"
                  :srcset="`${data.size_m} 320w, ${data.size_l} 640w`"
                  sizes="(max-width: 600px) 100vw"
                />
                <source
                  v-if="props.week.items_photo.length === 1"
                  :srcset="`${data.size_m} 640w, ${data.size_l} 1280w`"
                  sizes="(min-width: 601px) 50vw"
                />
                <source
                  v-else
                  :srcset="`${data.size_m} 320w, ${data.size_l} 640w`"
                  sizes="(min-width: 601px) 33vw"
                />
                <img loading="lazy"
                  :src="props.week.items_photo.length === 1 ? data.size_l : data.size_m"
                  :style="mediaStyle"
                  @click="openGallery(data.id)"
                >
              </picture>
            </template>
          </div>

          <NuxtLink :to="props.diary.link + '#weeks/' + props.week.id" class="more" v-if="props.week.items_photo.length > 6 && 5 == index">
            More photos +{{ props.week.items_photo.length - 6 }}
          </NuxtLink>
        </template>
      </UiCarousel>

    
    </div>
  
 
  </div>

  <GeneralLightboxVertical 
    v-if="lightboxShow"
    :photo-id="lightboxId" 
    :photos="lightboxPhotos" 
    @close="lightboxShow = false" 
  />
  
</template>

<script setup lang="ts">

import type { Diary, Week } from '@/types/diary';
import type { PhotoGallery } from '@/types/photo';
import { ref, onMounted, onBeforeUnmount, computed, nextTick } from 'vue';

interface Props {
  diary: Diary;
  week: Week;
}
const props = defineProps<Props>();

const lightboxShow = ref(false);
const lightboxId = ref<number | null>(null);
/** Root element for all slides of this GallerySlider instance */
const photosEl = ref<HTMLElement | null>(null);

/**
 * Uniform slide height (initially taken from the first media element)
 * so that the carousel does not "jump" while navigating.
 */
const slideHeight = ref<number | null>(null);

/** Inline style object applied to every <img loading="lazy"> / <video loading="lazy"> slide */
const mediaStyle = computed(() => (slideHeight.value ? { height: `${slideHeight.value}px` } : {}));

const galleryPhotos = computed(() => {
  
  // sort video first props.week.items_photo
  // props.week.items_photo.sort((a, b) => {
  //   if (a.is_video && !b.is_video) return -1;
  //   if (!a.is_video && b.is_video) return 1;
  //   return 0;
  // });


  let items = [];

  for (const p of props.week.items_photo) {    
    items.push(p);
  }

  // sort video items first
  items.sort((a, b) => { 
    if (a.is_video && !b.is_video) return -1;
    if (!a.is_video && b.is_video) return 1;
    return 0;
  });

  return items;


  

});

const lightboxPhotos = computed(() => {
  const items: Array<PhotoGallery> = [];

  if (!props.week.items_photo) return items;

  props.week.items_photo.sort((a, b) => { 
    if (a.video && !b.video) return -1;
    if (!a.video && b.video) return 1;
    return 0;
  });

  for (const p of props.week.items_photo) {
    const photo: PhotoGallery = {
      source: p,
      id: p.id,
      width: p.width,
      height: p.height,
      base: p.size_big,
      zoom: p.size_big,
      video: p.video,
      caption: p.comment,
    };
 

    items.push(photo);
  }

  // sort video items first
  // items.sort((a, b) => { 
  //   if (a.video && !b.video) return -1;
  //   if (!a.video && b.video) return 1;
  //   return 0;
  // });

  return items;
});

const openGallery = (key: number) => {
  if(useClientIsMobile().value) return;
  lightboxShow.value = true;
  lightboxId.value = key;
};

// Store references to every rendered <video loading="lazy"> element
const videos = ref<HTMLVideoElement[]>([]);

/**
 * Pause every tracked video except the one that should keep playing.
 * If `current` is omitted, pauses *all* videos.
 */
const pauseAllExcept = (current?: HTMLVideoElement | null) => {
  videos.value.forEach((v) => {
    if (!current || v !== current) {
      if (!v.paused) v.pause();
    }
  });
};

/**
 * Collects a <video loading="lazy"> element reference so that the IntersectionObserver can
 * start / stop playback depending on visibility.
 */
const setVideoRef = (el: HTMLVideoElement | null) => {
  if (el && !videos.value.includes(el)) {
    videos.value.push(el);
    // Observe immediately so that late‑rendered slides are handled too
    if (observer) observer.observe(el);
  }
};

/**
 * Detect the rendered height of the first slide’s media element
 * (either <img loading="lazy"> or <video loading="lazy">) and store it in `slideHeight`.
 * Re‑invoked on resize so that orientation changes are handled.
 */
const detectInitialHeight = () => {

  if(!useClientIsMobile().value) return;

  const firstMedia = photosEl.value?.querySelector('img, video') as
    | HTMLImageElement
    | HTMLVideoElement
    | null;
  if (!firstMedia) return;

  const applyHeight = () => {
    slideHeight.value = firstMedia.getBoundingClientRect().height || null;
  };

  if (firstMedia instanceof HTMLVideoElement) {
    if (firstMedia.readyState >= 1) {
      applyHeight();
    } else {
      firstMedia.addEventListener('loadedmetadata', applyHeight, { once: true });
    }
  } else {
    // HTMLImageElement
    if (firstMedia.complete) {
      applyHeight();
    } else {
      firstMedia.addEventListener('load', applyHeight, { once: true });
    }
  }
};

onMounted(() => {
  detectInitialHeight();
  window.addEventListener('resize', detectInitialHeight);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', detectInitialHeight);
});

let observer: IntersectionObserver | null = null;

// Create IntersectionObserver to auto‑play / pause videos like Instagram
onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target as HTMLVideoElement;
        if (entry.isIntersecting) {
          console.log('play');
          // Ensure only one video plays at any time
          pauseAllExcept(video);
          if (video.paused) {
            video.play().catch(() => {}); // ignore autoplay promise errors
          }
        } else {
          console.log('pause');
          if (!video.paused) {
            video.pause();
          }
        }
        // Show / hide play overlay
        const overlay = video.parentElement?.querySelector('.video_play') as HTMLElement | null;
        if (entry.isIntersecting) {
          overlay?.classList.add('hidden');
        } else {
          overlay?.classList.remove('hidden');
        }
      });
    },
    { threshold: 0.5 }
  );

  videos.value.forEach((video) => observer!.observe(video));
});

onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});

/**
 * Toggle play / pause when the slide is clicked. Falls back to lightbox
 * opening for images.
 */
const handleMediaClick = (event: MouseEvent, item: PhotoGallery) => {
  const slide = event.currentTarget as HTMLElement;
  const video = slide.querySelector('video');
  if (!video) {
    // Image slide – open the lightbox as before
    openGallery(item.id);
    return;
  }
  if ((video as HTMLVideoElement).paused) {
    pauseAllExcept(video as HTMLVideoElement);
    (video as HTMLVideoElement).play().catch(() => {});
  } else {
    (video as HTMLVideoElement).pause();
  }
  // Toggle overlay visibility
  const overlay = slide.querySelector('.video_play') as HTMLElement | null;
  overlay?.classList.toggle('hidden', !(video as HTMLVideoElement).paused);
};

/**
 * Toggle the muted state of the video that owns the clicked button and
 * update the button’s visual state.
 */
const toggleMute = (event: MouseEvent) => {
  const btn = event.currentTarget as HTMLElement;
  const video = btn.closest('.photo')?.querySelector('video') as HTMLVideoElement | null;
  if (!video) return;

  video.muted = !video.muted;
  // Add / remove a marker class so CSS can switch icons
  btn.classList.toggle('muted', video.muted);
};
 
</script>



<style scoped>

 .slider{
  /* margin-bottom: 2rem; */
 }
.galpan{

  display: flex;
    flex-direction: column;
    gap: 4rem;
    position: relative;
}
.cover{
  width: 100%;
  height: 100%;
  object-fit: cover;
  aspect-ratio: 4/3;
    border-radius: 5px;
}

/* week */

.week{
  font-weight: bold;
  padding-bottom: 4px;
  position: absolute;
  z-index: 1;
  background-color: #ffffffe6;
  border-radius: 5px;
  padding: 4px 12px;
  position: sticky;
  top: 60px;
  width: fit-content;
  margin-top: 12px;
  margin-bottom: -40px;
  font-size: 1rem;
  margin-left: 10px;
}
/* photos */
.photos:deep(.slide-counter),
.photos:deep(.dots-wrapper){
  display: none!important;
}
.photos:deep(.slides){
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  position: relative;
  /* aspect-ratio: 1 / 1; */
  aspect-ratio: none;
} 
.photos .more{
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: var(--un-background-color);
  padding: 4px 8px;
  border-radius: 5px;
  cursor: pointer;
  /* color: var(--un-text-color-gray); */
  /* font-weight: bold; */
  z-index: 1;
  margin-right: 10px;
  margin-bottom: 10px;
  transition: 0.3s;

} 
.photos:deep(.slides .slide){
  display: flex;
  flex-wrap: wrap;
  aspect-ratio: 4/3;
  flex: 1 1 calc(100%/3 - 4px);
  /* remove id need 1 big photo */
  max-width: calc(100%/3 - 4px);
}
.photos .photo {
  width: 100%;
  height: 100%;
}


.video_play {
  width: 56px;
  height: 56px;
  z-index: 3;
  position: absolute;
  background-image: url(/images/play_button.png);
  background-repeat: no-repeat;
  background-position: 50% 50%;
  background-size: 62px;
  left: calc(50% - 28px);
  top: calc(50% - 28px);
  pointer-events: none;
}
.photos .photo picture{
  width:100%;
  height:100%;
}

.photos .photo img{
  width:100%;
  height:100%;
  object-fit: cover;
  border-radius: 5px;
}

.photos img:only-child {
  flex-basis: 100%;
}







/* .photos .photo:nth-last-child(2):first-child,
.photos .photo:nth-last-child(2):last-child {
    flex-basis: calc(100%/3 - 4px);
} */

@container pb (max-width: 600px) {

  .photos:deep(.slide-counter),
  .photos:deep(.dots-wrapper){
    display: flex!important;
  }
  .photos:deep(.slides){
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    flex-wrap: nowrap;
    margin-left: -20px;
    width: calc(100% + 40px);
    gap: 0;
  }
  .photos::-webkit-scrollbar {
    display: none;
  }
  .photos:deep(.slides .slide){
    aspect-ratio: inherit;
    flex-shrink: 0;
    width: 100% !important;
    flex: none;
    max-width: none;
    align-items: center;
    contain: layout;
    justify-content: center;
    position: relative;
    scroll-snap-align: center;
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }
  .photos .photo video,
  .photos .photo img{
    aspect-ratio: inherit;
    max-height: 700px;
    min-height: 100dvw;
    -o-object-fit: cover;
    object-fit: cover;
    width: 100%;
    border-radius: 0;
  } 
  .week{
    margin-left: 0;
  }
}


/* SIZES GALLERY */
@container pb (min-width: 600px) {
  .photos[size="1"] :deep(.slides .slide){
    max-width: 70%;
    flex: 1 1 100%;
    margin: 0 auto;
  }
  .photos[size="2"] :deep(.slides .slide){
    max-width: calc(100%/2 - 4px);
    flex: 1 1 calc(100%/2 - 4px);
  }
  .photos[size="3"] :deep(.slides .slide){
    max-width: calc(100%/3 - 4px);
    flex: 1 1 calc(100%/3 - 4px);
  }
  .photos[size="4"] :deep(.slides .slide){
    max-width: calc(100%/2 - 4px);
    flex: 1 1 calc(100%/2 - 4px);
  }
  .photos[size="5"] :deep(.slides .slide):nth-child(1),
  .photos[size="5"] :deep(.slides .slide):nth-child(1),
  .photos[size="5"] :deep(.slides .slide):nth-child(3){
    max-width: calc(100%/3 - 4px);
    flex: 1 1 calc(100%/3 - 4px);
  }
  .photos[size="5"] :deep(.slides .slide):nth-child(4),
  .photos[size="5"] :deep(.slides .slide):nth-child(5){
    max-width: calc(100%/2 - 4px);
    flex: 1 1 calc(100%/2 - 4px);
  }
}



.dots{
  display: flex;
  gap: 4px;
  position: relative;
  margin-top: -20px;
  margin-bottom: 20px;
  
  justify-content: center;
  z-index: 1;
}

/* .dot{
  width: 7px;
  height: 7px;
  background-color: rgb(159, 159, 159);
  border-radius: 50%;
}

@container pb (min-width: 600px) {
  .dots{
    display: none;
  }
} */

/* Video slide styles */
.photo.is-video {
  position: relative;
}

.autoplay-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 5px;
}

/* Mute / unmute button bottom‑right */
.photo.is-video .mute-toggle {
  position: absolute;
    bottom: 12px;
    right: 8px;
    width: 29px;
    height: 29px;
    cursor: pointer;
    z-index: 4;
    font-size: 1rem;
    background-color: #0000007d;
    color: white;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1rem;
}

/* Show the correct volume icon depending on whether the video is muted */
.mute-toggle .icon-volume-x,
.mute-toggle .icon-volume-2 {
  pointer-events: none;
}

.mute-toggle:not(.muted) .icon-volume-x {
  display: none;
}

.mute-toggle.muted .icon-volume-2 {
  display: none;
}

/* Hide play overlay when video is playing */
.video_play.hidden {
  display: none;
}


</style>
